// useVisualViewport — React hook for iOS Safari's dynamic bottom chrome.
//
// Why this exists (CLAUDE.md §5 rule #5):
//   iOS Safari renders ~50pt of URL bar at the bottom on cold load, then
//   collapses it after the user scrolls. The home-indicator (~34pt) is
//   covered by env(safe-area-inset-bottom), but the URL bar is NOT — it
//   lives in the gap between window.innerHeight (layout viewport) and
//   visualViewport.height (the actually-visible area).
//
//   Same gap appears when the soft keyboard opens (~270pt). 100dvh adapts
//   to chrome show/hide but does NOT shrink when the keyboard appears.
//
// We expose `bottomChrome = window.innerHeight - visualViewport.height` so
// fixed-positioned elements (bottom nav, sheets, action bars) can pad
// themselves above it via `max(env(safe-area-inset-bottom), var(--vv-bottom-chrome))`.
//
// References:
//   https://webkit.org/blog/12492/working-with-visualviewport/
//   https://developer.mozilla.org/en-US/docs/Web/API/VisualViewport

import { useEffect, useState } from 'react'

export type ViewportInsets = {
  /** Visible viewport height (excludes URL bar + keyboard) */
  height: number
  /** Visible viewport width */
  width: number
  /** How far the visual viewport is offset from the layout viewport top */
  offsetTop: number
  /**
   * Layout-vs-visual delta at the bottom edge. > 0 when the iOS Safari URL
   * bar is showing OR the keyboard is open. Treat as "extra pad needed".
   */
  bottomChrome: number
}

const ZERO_INSETS: ViewportInsets = {
  height: 0,
  width: 0,
  offsetTop: 0,
  bottomChrome: 0,
}

/**
 * Pure helper — exported for unit-testing.
 * Snapshots a VisualViewport-like object + layout-viewport height into a
 * stable insets object. Whole-pixel rounding so the hook doesn't churn
 * once per frame during chrome animations.
 */
export function computeInsets(
  layout: { innerHeight: number; innerWidth: number },
  vv: { height: number; width: number; offsetTop: number } | null,
): ViewportInsets {
  if (!vv) {
    return {
      height: layout.innerHeight,
      width: layout.innerWidth,
      offsetTop: 0,
      bottomChrome: 0,
    }
  }
  const height = Math.round(vv.height)
  const width = Math.round(vv.width)
  const offsetTop = Math.round(vv.offsetTop)
  // Clamp at 0 — a few iOS builds report visualViewport.height slightly
  // greater than innerHeight during rotation transients, which would push
  // a negative pad and lift sticky elements off-screen.
  const bottomChrome = Math.max(0, layout.innerHeight - height - offsetTop)
  return { height, width, offsetTop, bottomChrome }
}

function readInsets(): ViewportInsets {
  if (typeof window === 'undefined') return ZERO_INSETS
  return computeInsets(
    { innerHeight: window.innerHeight, innerWidth: window.innerWidth },
    window.visualViewport,
  )
}

/**
 * Subscribes to window.visualViewport resize + scroll. Returns current insets.
 * SSR-safe — returns zeros on the server. Hook re-renders only when an inset
 * value actually changes (whole-pixel snapshots, see computeInsets).
 */
export function useVisualViewport(): ViewportInsets {
  const [insets, setInsets] = useState<ViewportInsets>(() => readInsets())

  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return

    let raf = 0
    function update() {
      if (raf) cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        const next = readInsets()
        setInsets((prev) =>
          prev.height === next.height &&
          prev.width === next.width &&
          prev.offsetTop === next.offsetTop &&
          prev.bottomChrome === next.bottomChrome
            ? prev
            : next,
        )
      })
    }

    // 'resize' fires for keyboard open/close + URL bar show/hide.
    // 'scroll' fires when the visual viewport pans (keyboard-pinch).
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    // Also listen on window for orientation changes that miss vv events on
    // older iOS builds.
    window.addEventListener('orientationchange', update)
    // Initial read post-mount in case SSR/hydration produced stale insets.
    update()
    return () => {
      if (raf) cancelAnimationFrame(raf)
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
      window.removeEventListener('orientationchange', update)
    }
  }, [])

  return insets
}
